import React from 'react';
import { PageSection, SiteData } from '../../../types';
import { Star, MessageSquare, Sparkles } from 'lucide-react';
import { isDarkBackground, getCardStylesForBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
}

export const TestimonialsSection: React.FC<SectionProps> = ({ section, siteData }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'MÜŞTERİ YORUMLARI';
  const title = content.title || 'Müşterilerimizin Başarı Hikayeleri';
  
  // Read testimonials from content or site settings
  const settingsList = (siteData.settings as any)?.testimonialsList || (siteData.settings as any)?.testimonials;
  const rawItems = content.items || (Array.isArray(settingsList) ? settingsList : []);
  const items = Array.isArray(rawItems) ? rawItems : [];

  const bgColor = style.backgroundColor || '#F8FAFC';
  const isDark = isDarkBackground(bgColor);
  const cardStyles = getCardStylesForBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const columnsCount = Number(content.columns) || 2;
  const gridColsClass =
    columnsCount === 3
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      : 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto';

  const anchorId = style.anchorId || style.customId || 'testimonials';

  return (
    <section
      id={anchorId}
      data-section-id={section.id}
      data-section-type="TESTIMONIALS"
      className="py-20 px-4 sm:px-6 lg:px-8 select-none transition-colors"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          {badge && (
            <div
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-black tracking-widest uppercase border"
              style={{
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}10`,
                color: accentColor,
              }}
            >
              <Sparkles className="w-3 h-3" />
              <span>{badge}</span>
            </div>
          )}
          <h2 className={`text-2xl sm:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h2>
        </div>

        {items.length === 0 ? (
          <div className="p-12 text-center rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800 max-w-md mx-auto space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 flex items-center justify-center mx-auto">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h3 className={`text-sm font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Henüz Müşteri Yorumu Bulunmuyor</h3>
              <p className="text-xs text-slate-400">
                Memnun müşterilerinizin geri bildirimlerini sol panelden ekleyerek güven oluşturun.
              </p>
            </div>
          </div>
        ) : (
          <div className={`grid ${gridColsClass} gap-8`}>
            {items.map((item: any, idx: number) => (
              <div
                key={item.id || idx}
                className={`p-8 rounded-3xl border shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all space-y-6 flex flex-col justify-between ${cardStyles.bg} ${cardStyles.border}`}
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(item.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400" />
                    ))}
                  </div>
                  {item.title && (
                    <h3 className={`font-bold text-base ${cardStyles.heading}`}>
                      {item.title}
                    </h3>
                  )}
                  <p className={`text-sm leading-relaxed italic ${cardStyles.mutedText}`}>
                    "{item.description}"
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200/40 dark:border-slate-800 flex items-center gap-3">
                  {item.avatarUrl ? (
                    <img
                      src={item.avatarUrl}
                      alt={item.author}
                      className="w-11 h-11 rounded-full object-cover border border-slate-200 dark:border-slate-700"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white text-xs shadow-sm"
                      style={{ backgroundColor: accentColor }}
                    >
                      {item.author?.charAt(0) || 'M'}
                    </div>
                  )}
                  <div>
                    <div className={`font-bold text-sm ${cardStyles.heading}`}>{item.author}</div>
                    <div className={`text-xs ${cardStyles.mutedText}`}>{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

